/**
 * Pure metric calculators for the Properties page cards + KPI row.
 *
 * Mirrors the design's `computeMetrics` and the strategy-specific tile rules
 * in `design/Syntra/properties.html`. The card decides which 3 tiles to show
 * based on `strategy`:
 *
 *   - Flip    → Estimated profit · Holding cost · Profit margin
 *   - Rental  → Monthly cash flow · DSCR · 1% rule
 *   - BRRRR   → Monthly cash flow · DSCR · Net cash-out (post-refi view)
 *
 * Source of truth is `property_setting`. We honor saved deal inputs where
 * present and fall back to the same heuristics the detail page uses — except
 * rehab cost, which is strictly actual: only COMPLETED `rehab_line_items`
 * contribute, summed on `actual_cost`, with no estimate fallback.
 */
import {
  amortizedEmi,
  capRate as capRateFn,
  closingCost as closingCostFn,
  computeMonthlyCashflow,
  interestOnlyMonthly,
  loanAmount as loanAmountFn,
  onePctRule,
  termInMonths,
} from "@/lib/calculators";
import { resolveSellingCost } from "@/lib/property-setting";
import type { LoanMeta } from "@/components/financing/financing-metrics";
import type { DealSettingsRow } from "@/hooks/use-deal-settings";
import type { CashflowOverrides, Strategy } from "@/types/pipeline";

export type PropertyStatus = "in_rehab" | "owned" | "sold";

export interface MetricBadge {
  tone: "good" | "warn" | "bad";
  label: string;
}

export interface MetricTile {
  key: string;
  label: string;
  /** Subtitle line under the label (e.g. "NOI ÷ price · target ≥ 7%"). */
  detail: string;
  /** Big right-side value (e.g. "$1,250"). */
  value: string;
  /** Unit line under the value (e.g. "per month"). */
  unit: string;
  /** Optional pill next to the label. */
  badge?: MetricBadge;
  /** Value text color tone. */
  valueTone?: "pos" | "neg" | "warn";
}

export interface PropertyMetrics {
  strategy: Strategy;
  /** Resolved purchase price (may differ from listed). */
  purchase: number;
  /** Resolved after-repair value. */
  arv: number;
  /** Actual rehab spend — Σ `actual_cost` of COMPLETED line items only. */
  rehab: number;
  /** Monthly rent. */
  rent: number;
  /** Annual NOI (cashflow model — honors saved overrides). */
  annualNoi: number;
  /** Monthly cash flow (NOI − debt service for the active loan view). */
  monthlyCashFlow: number;
  /** Cap rate (percent). */
  capRate: number;
  /** Days owned since rehab started. */
  daysOwned: number;
  /** Three tiles to render on the card, in display order. */
  tiles: MetricTile[];
}

/** Minimal `rehab_line_items` shape needed to total recognized rehab spend. */
export interface RehabActualRow {
  actual_cost: number | null;
  status: string | null;
}

/**
 * Recognized rehab spend — the sum of `actual_cost` across COMPLETED
 * `rehab_line_items` rows.
 *
 * Pending rows contribute nothing regardless of what has been recorded against
 * them, and there is NO estimate fallback: a property with no completed work
 * has spent $0 on rehab so far, not a guess. Mirrors `recognizedSpend` in
 * `@/components/rehab/rehab-scope`, so the property card, the rehab drawer, and
 * the "Budget Used" bar all report the same figure.
 */
function sumCompletedRehabActuals(
  rows: RehabActualRow[] | null | undefined,
): number {
  return (rows ?? []).reduce(
    (sum, r) =>
      sum + (r.status === "completed" ? Number(r.actual_cost) || 0 : 0),
    0,
  );
}

function fmtCurrency(value: number): string {
  const sign = value < 0 ? "-" : "";
  return sign + "$" + Math.abs(Math.round(value)).toLocaleString();
}

function fmtPercent(value: number, digits = 1): string {
  if (!Number.isFinite(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

const TARGET = {
  monthlyCashFlow: { good: 100, ok: 0 },
  dscr: { good: 1.25, ok: 1.0 },
  onePct: { good: 1.0, ok: 0.8 },
  capRate: { good: 7, ok: 5 },
  flipProfit: { good: 25_000, ok: 0 },
  flipMargin: { good: 15, ok: 5 },
} as const;

function rank(value: number, good: number, ok: number): MetricBadge {
  if (value >= good) return { tone: "good", label: "Above target" };
  if (value >= ok) return { tone: "warn", label: "At threshold" };
  return { tone: "bad", label: "Below target" };
}

function badgeValueTone(badge: MetricBadge): "pos" | "neg" | "warn" {
  if (badge.tone === "bad") return "neg";
  if (badge.tone === "warn") return "warn";
  return "pos";
}

/**
 * Days elapsed since the rehab started (ISO date string from
 * `rehab_projects.start_date`). Clamped to 0.
 */
export function daysBetween(startIso: string | null | undefined): number {
  if (!startIso) return 0;
  const start = new Date(startIso).getTime();
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, Math.floor((Date.now() - start) / (1000 * 60 * 60 * 24)));
}

interface ComputeArgs {
  settings: DealSettingsRow | null;
  /** From `property_setting.purchase_price` OR `pipeline_properties.purchase_price`. */
  fallbackPurchase: number;
  strategy: Strategy | null;
  /** ISO date — `rehab_projects.start_date`. Fallback for "Owned X days". */
  rehabStartDate: string | null;
  /** ISO date — `pipeline_properties.owned_date`. Preferred for "Owned X days". */
  ownedDate?: string | null;
  /**
   * Actual rent collected in the latest month that has a `rent_payments`
   * record (status = paid). This is the ONLY rent source — every rent-derived
   * metric (cash flow, DSCR, cap rate, 1% rule, and the percentage expense
   * reserves) keys off it. Null/absent means no paid rent on record, which is
   * treated as $0 rent — we never fall back to the projected
   * `property_setting.monthly_rent`.
   */
  actualMonthlyRent?: number | null;
  /**
   * Financing metadata from the `loans` table. Absent ⇒ the deal defaults to
   * financed (scenario terms). An explicit all-cash / disabled loan is a row
   * with `is_enabled === false` — in that case debt service is $0 (no monthly
   * payment), so cash flow equals NOI.
   */
  loan?: LoanMeta | null;
  /**
   * `rehab_line_items` rows for the property. The ONLY rehab-cost source: the
   * completed rows' `actual_cost` are summed and pending rows count as $0.
   * Absent/empty means no completed work on record ⇒ $0 rehab — we never fall
   * back to `property_setting.repair_cost` or a percent-of-purchase estimate.
   */
  rehabLineItems?: RehabActualRow[] | null;
}

/** Compute every metric the card needs in one call. */
export function computePropertyMetrics(args: ComputeArgs): PropertyMetrics {
  const strategy: Strategy = args.strategy ?? "BRRRR";

  // ── Resolved inputs (honor saved settings, else heuristics) ─────────────
  const purchase =
    Number(args.settings?.purchase_price) || Number(args.fallbackPurchase) || 0;
  const arv =
    Number(args.settings?.after_repair_value) || Math.round(purchase * 1.28);

  // Rehab cost is STRICT ACTUAL — only line items marked completed, summed on
  // their `actual_cost`. No estimate fallback (`repair_cost`, 18% of purchase):
  // work that hasn't been finished hasn't cost anything yet, so a property with
  // no completed items carries $0 of rehab.
  const rehab = sumCompletedRehabActuals(args.rehabLineItems);

  // ── Cash-flow model — STRICT ACTUAL rent ─────────────────────────────────
  // Rent income comes only from real `rent_payments`; an unpaid month is $0.
  // `monthly_rent` is never used (neither as income nor as an expense basis).
  const rent = Number(args.actualMonthlyRent) || 0;
  const cfo: CashflowOverrides = args.settings?.monthly_cash_flow ?? {};
  // When there are NO saved overrides (monthly_cash_flow is null or `{}`), every
  // expense line is treated as $0 — we do NOT synthesize reserves from rent /
  // purchase heuristics. The formula fallbacks below only apply once the user
  // has begun entering overrides (so a partially-filled object still fills gaps).
  const hasOverrides = Object.keys(cfo).length > 0;
  const cfv = (k: keyof CashflowOverrides, fallback: number) => {
    const raw = cfo[k];
    if (raw !== undefined) return Number(raw) || 0;
    return hasOverrides ? fallback : 0;
  };
  const cashflow = computeMonthlyCashflow({
    gross: rent,
    other: cfv("other", 0),
    vacancy: cfv("vacancy", rent * 0.05),
    mgmt: cfv("mgmt", rent * 0.08),
    taxes: cfv("taxes", purchase * 0.00103),
    insurance: cfv("insurance", purchase * 0.0005),
    maint: cfv("maint", rent * 0.08),
    capex: cfv("capex", rent * 0.06),
    hoa: cfv("hoa", 0),
    utilities: cfv("utilities", 0),
  });
  const noiMonthly = cashflow.noi;
  const annualNoi = noiMonthly * 12;

  // ── Strategy financing (uses BRRRR long-term / Rental long_term blob) ────
  // A disabled loan (`loans.is_enabled === false`) means the property is held
  // all-cash: no debt service, so every monthly payment below is $0. Absent
  // metadata defaults to financed (scenario terms).
  const loanEnabled = args.loan ? args.loan.is_enabled : true;
  const brrrrLong = args.settings?.brrrr?.long_term;
  const brrrrShort = args.settings?.brrrr?.hard_money;
  const rentalLong = args.settings?.rental?.long_term;
  const longLoan = strategy === "Rental" ? rentalLong : brrrrLong;
  const ltvPct = Number(longLoan?.ltv) || 75;
  const ratePct = Number(longLoan?.rate) || 7;
  const longTermMonths = (longLoan && termInMonths(longLoan, "years")) || 360;

  // ── BRRRR refi (long view) — refi loan on ARV × LTV, amortized over years. ─
  const refiLoan = loanAmountFn(arv, ltvPct);
  const refiEmi = amortizedEmi(refiLoan, ratePct, longTermMonths);

  // ── BRRRR hard money (short view) — loan on purchase × LTV, term in months. ─
  // Interest-only carry, not amortized: the principal balloons at the refi, so
  // amortizing it would charge principal monthly AND again at payoff.
  const brrrrShortLtv = Number(brrrrShort?.ltv) || 85;
  const brrrrShortRate = Number(brrrrShort?.rate) || 11;
  const hmlLoan = loanAmountFn(purchase, brrrrShortLtv);
  const hmlEmi = interestOnlyMonthly(hmlLoan, brrrrShortRate);

  // Active BRRRR slot follows the loans switcher: `loan_type === "hard_money"`
  // (while enabled) uses the hard-money payment; refinance / default uses the
  // long-term refi. Mirrors `resolveLoanScenario` so the card, detail view, and
  // chart all agree. Disabled loan ⇒ $0 debt service (held all-cash).
  const brrrrHardMoneyActive = args.loan?.loan_type === "hard_money";
  const brrrrDebt = loanEnabled ? (brrrrHardMoneyActive ? hmlEmi : refiEmi) : 0;
  const mcfBrrrr = noiMonthly - brrrrDebt;
  const dscrBrrrr = brrrrDebt > 0 ? noiMonthly / brrrrDebt : 0;
  // Net cash-out at refi (refi loan minus original HML and approximate closing
  // costs — purposely simplified for the card; the detail page has the full
  // form). Negative means cash left in the deal.
  const netCashOut = refiLoan - hmlLoan - refiLoan * 0.01;

  // ── Rental loan (conventional, same long blob) ─────────────────────────
  const rentLoanAmt = loanAmountFn(purchase, ltvPct);
  const rentEmi = loanEnabled
    ? amortizedEmi(rentLoanAmt, ratePct, longTermMonths)
    : 0;
  const mcfRental = noiMonthly - rentEmi;
  const dscrRental = rentEmi > 0 ? noiMonthly / rentEmi : 0;
  const onePct = onePctRule(rent, purchase);

  // ── Flip ────────────────────────────────────────────────────────────────
  const flip = args.settings?.flip;
  const flipHm = flip?.hard_money;
  const flipLtv = Number(flipHm?.ltv) || 85;
  const flipRate = Number(flipHm?.rate) || 11;
  const flipPts = Number(flipHm?.points) || 0;
  const flipLoan = loanAmountFn(purchase, flipLtv);
  // Interest-only carry — see the BRRRR hard-money note above. This feeds
  // `flipHolding`, which multiplies by the hold months.
  const flipEmi = loanEnabled ? interestOnlyMonthly(flipLoan, flipRate) : 0;
  const flipHoldMonths = Number(flip?.hold_months) || 0;
  const flipTax = flip?.taxes_monthly ?? 0;
  const flipUtil = flip?.utilities_monthly ?? 0;
  const flipHolding =
    (flipEmi + Number(flipTax) + Number(flipUtil)) * flipHoldMonths;
  // Buy-side closing costs use the deal's saved `closing_pct` (the same input
  // the Find detail page edits); 3% only when the row has none yet.
  const closingCost = closingCostFn(
    purchase,
    Number(args.settings?.closing_pct) || 0,
  );
  const flipOrig = flipLoan * (flipPts / 100);
  // The entered dollar amount, falling back to the percent of ARV for rows
  // that predate it (`resolveSellingCost`).
  const { value: selling } = resolveSellingCost(flip, arv);
  const flipProfit =
    arv - (purchase + rehab + closingCost + flipOrig + flipHolding + selling);
  const flipMargin = arv > 0 ? (flipProfit / arv) * 100 : 0;

  // ── Tiles per strategy ──────────────────────────────────────────────────
  const tiles: MetricTile[] = [];

  if (strategy === "Flip") {
    const b1 = rank(flipProfit, TARGET.flipProfit.good, TARGET.flipProfit.ok);
    tiles.push({
      key: "profit",
      label: "Estimated profit",
      detail: "ARV minus all-in costs",
      value: fmtCurrency(flipProfit),
      unit: "after sale",
      badge: b1,
      valueTone: badgeValueTone(b1),
    });
    tiles.push({
      key: "holding",
      label: "Holding cost",
      detail: "Carry through sale",
      value: fmtCurrency(flipHolding),
      unit: `over ${flipHoldMonths} mo`,
    });
    const b3 = rank(flipMargin, TARGET.flipMargin.good, TARGET.flipMargin.ok);
    tiles.push({
      key: "margin",
      label: "Profit margin",
      detail: "Profit ÷ ARV · target ≥ 15%",
      value: fmtPercent(flipMargin),
      unit: "of ARV",
      badge: b3,
      valueTone: badgeValueTone(b3),
    });
  } else if (strategy === "Rental") {
    const b1 = rank(
      mcfRental,
      TARGET.monthlyCashFlow.good,
      TARGET.monthlyCashFlow.ok,
    );
    tiles.push({
      key: "mcf",
      label: "Monthly cash flow",
      detail: `${fmtCurrency(mcfRental * 12)} per year`,
      value: fmtCurrency(mcfRental),
      unit: "per month",
      badge: b1,
      valueTone: badgeValueTone(b1),
    });
    const b2 = rank(dscrRental, TARGET.dscr.good, TARGET.dscr.ok);
    tiles.push({
      key: "dscr",
      label: "DSCR",
      detail: "NOI ÷ debt service · target ≥ 1.25",
      value: dscrRental.toFixed(2),
      unit: "ratio",
      badge: b2,
      valueTone: badgeValueTone(b2),
    });
    const b3 = rank(onePct, TARGET.onePct.good, TARGET.onePct.ok);
    tiles.push({
      key: "onepct",
      label: "1% rule",
      detail: "Monthly rent ÷ price · target ≥ 1.0%",
      value: `${onePct.toFixed(2)}%`,
      unit: "of price",
      badge: b3,
      valueTone: badgeValueTone(b3),
    });
  } else {
    // BRRRR — the loans switcher decides hard-money vs refinanced debt service.
    const b1 = rank(
      mcfBrrrr,
      TARGET.monthlyCashFlow.good,
      TARGET.monthlyCashFlow.ok,
    );
    tiles.push({
      key: "mcf",
      label: "Monthly cash flow",
      detail: `${fmtCurrency(mcfBrrrr * 12)} per year`,
      value: fmtCurrency(mcfBrrrr),
      unit: "per month",
      badge: b1,
      valueTone: badgeValueTone(b1),
    });
    const b2 = rank(dscrBrrrr, TARGET.dscr.good, TARGET.dscr.ok);
    tiles.push({
      key: "dscr",
      label: "DSCR",
      detail: "NOI ÷ debt service · target ≥ 1.25",
      value: dscrBrrrr.toFixed(2),
      unit: "ratio",
      badge: b2,
      valueTone: badgeValueTone(b2),
    });
    const b3: MetricBadge =
      netCashOut >= 0
        ? { tone: "good", label: "Positive" }
        : { tone: "bad", label: "Negative" };
    tiles.push({
      key: "netcashout",
      label: "Net cash-out",
      detail: "Capital returned at refinance",
      value: fmtCurrency(netCashOut),
      unit: "at refi",
      badge: b3,
      valueTone: badgeValueTone(b3),
    });
  }

  return {
    strategy,
    purchase,
    arv,
    rehab,
    rent,
    annualNoi,
    monthlyCashFlow:
      strategy === "Rental" ? mcfRental : strategy === "Flip" ? 0 : mcfBrrrr,
    capRate: capRateFn(annualNoi, purchase),
    daysOwned: daysBetween(args.ownedDate ?? args.rehabStartDate),
    tiles,
  };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * KPI roll-up across all properties on the page
 * ────────────────────────────────────────────────────────────────────────── */

export interface PortfolioKpis {
  count: number;
  buckets: Record<PropertyStatus, number>;
  totalValue: number;
  totalMonthlyCashFlow: number;
  avgCapRate: number;
  /** Number of properties contributing to avg cap rate. */
  capRateSampleCount: number;
}

export function aggregateKpis(
  entries: Array<{ metrics: PropertyMetrics; status: PropertyStatus }>,
): PortfolioKpis {
  const buckets: Record<PropertyStatus, number> = {
    in_rehab: 0,
    owned: 0,
    sold: 0,
  };
  let totalValue = 0;
  let totalMonthlyCashFlow = 0;
  let capRateSum = 0;
  let capRateSampleCount = 0;

  for (const { metrics, status } of entries) {
    buckets[status] += 1;
    totalValue += metrics.arv;
    // Skip sold from active cash-flow + cap-rate roll-up (matches design).
    if (status !== "sold") {
      totalMonthlyCashFlow += metrics.monthlyCashFlow;
      if (metrics.capRate > 0) {
        capRateSum += metrics.capRate;
        capRateSampleCount += 1;
      }
    }
  }

  return {
    count: entries.length,
    buckets,
    totalValue,
    totalMonthlyCashFlow,
    avgCapRate: capRateSampleCount > 0 ? capRateSum / capRateSampleCount : 0,
    capRateSampleCount,
  };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Monthly performance series (powers the "Monthly performance" chart)
 * ────────────────────────────────────────────────────────────────────────── */

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** One trailing-month bucket: rent, total outflows, and resulting cash flow. */
export interface MonthlyPoint {
  month: string;
  rent: number;
  expenses: number;
  cashflow: number;
}

/** Input for one property contributing to the chart. */
export interface SeriesInput {
  /** Stable id — seeds the per-month variation so the trend is deterministic. */
  id: string;
  /** Base monthly rent. */
  rent: number;
  /** Base monthly cash flow (strategy-aware — `metrics.monthlyCashFlow`). */
  cashflow: number;
}

/**
 * djb2 string hash → non-negative integer. Used only to seed a *deterministic*
 * month-to-month wobble so the chart reads as a believable trend rather than a
 * flat line — we don't persist real monthly history.
 */
function hashSeed(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = (h << 5) + h + s.charCodeAt(i);
  return Math.abs(h);
}

/** Deterministic 1 ± pct factor seeded by (id, month, salt). */
function variation(
  id: string,
  monthIdx: number,
  salt: string,
  pct: number,
): number {
  const r = (hashSeed(`${id}:${monthIdx}:${salt}`) % 1000) / 1000; // 0..1
  return 1 + (r * 2 - 1) * pct;
}

/**
 * Synthesize 12 trailing monthly buckets across the given properties. We only
 * store base rent + cash flow per property, so each month gets a small (±3%),
 * deterministic variation. Monthly expenses are derived as `rent − cashflow`
 * so the chart's math stays internally consistent (rent − expenses = cashflow).
 *
 * Mirrors `buildMonthlySeries` in `design/Syntra/properties.html`.
 */
export function buildMonthlySeries(
  inputs: SeriesInput[],
  now: Date = new Date(),
): MonthlyPoint[] {
  const points: MonthlyPoint[] = [];
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    points.push({
      month: MONTH_NAMES[d.getMonth()],
      rent: 0,
      expenses: 0,
      cashflow: 0,
    });
  }

  for (const input of inputs) {
    const baseRent = input.rent || 0;
    const baseExp = baseRent - (input.cashflow || 0); // total monthly outflows
    for (let i = 0; i < 12; i += 1) {
      const r = Math.round(baseRent * variation(input.id, i, "r", 0.03));
      const e = Math.round(baseExp * variation(input.id, i, "e", 0.03));
      points[i].rent += r;
      points[i].expenses += e;
      points[i].cashflow += r - e;
    }
  }

  return points;
}
